// Local storage utilities for auth
const TOKEN_KEY = 'magicstay_token';
const USER_KEY = 'magicstay_user';
const FAVORITES_CACHE_KEY = 'magicstay_favorites_cache';
const PROPERTIES_CACHE_KEY = 'magicstay_properties_cache';

export const storage = {
  // Token methods
  getToken: () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  },

  setToken: (token) => {
    try {
      if (token) {
        localStorage.setItem(TOKEN_KEY, token);
      }
    } catch (error) {
      console.error('Error setting token:', error);
    }
  },

  // User methods
  getUser: () => {
    try {
      const user = localStorage.getItem(USER_KEY);
      return user ? JSON.parse(user) : null;
    } catch (error) {
      console.error('Error getting user:', error);
      return null;
    }
  },

  setUser: (user) => {
    try {
      if (user) {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        // Dispatch event to notify components
        window.dispatchEvent(new Event('storage'));
        window.dispatchEvent(new CustomEvent('authChanged'));
      }
    } catch (error) {
      console.error('Error setting user:', error);
    }
  },

  // Clear auth and ALL user data (Bug 13 fix)
  clearAuth: () => {
    try {
      // Remove auth tokens
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      
      // Remove ALL user-specific data to prevent data leakage after logout
      const userDataKeys = [
        'favorites',
        'compare_properties', 
        'recent_views',
        'favorite_notes',
        'property_categories',
        'favorite_categories',
        'saved_searches',
        FAVORITES_CACHE_KEY,
        PROPERTIES_CACHE_KEY
      ];
      
      userDataKeys.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Clear ALL property drafts (for all users, since we're logging out)
      // This prevents drafts from one user appearing for another
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('magicstay_property_draft_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Keep only language preference
      // localStorage.getItem('language') is preserved
      
      // Dispatch event to notify components
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new CustomEvent('authChanged'));
      
      console.log('[Storage] Auth and user data cleared');
    } catch (error) {
      console.error('Error clearing auth:', error);
    }
  },

  // Check if logged in
  isLoggedIn: () => {
    const token = storage.getToken();
    const user = storage.getUser();
    return !!(token && user);
  },

  // Check if admin
  isAdmin: () => {
    const user = storage.getUser();
    return user?.role === 'admin';
  },

  // ============================
  // OFFLINE CACHE METHODS
  // ============================

  // Cache favorites for offline access
  cacheFavorites: (favorites) => {
    try {
      localStorage.setItem(FAVORITES_CACHE_KEY, JSON.stringify({
        data: favorites,
        timestamp: Date.now()
      }));
      
      // Also notify Service Worker to cache images
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_FAVORITES',
          payload: { favorites }
        });
      }
      
      console.log('[Storage] Favorites cached for offline use');
    } catch (error) {
      console.error('Error caching favorites:', error);
    }
  },

  // Get cached favorites
  getCachedFavorites: () => {
    try {
      const cached = localStorage.getItem(FAVORITES_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Cache valid for 24 hours
        if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
          return data;
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting cached favorites:', error);
      return null;
    }
  },

  // Cache properties for offline access
  cacheProperties: (properties) => {
    try {
      localStorage.setItem(PROPERTIES_CACHE_KEY, JSON.stringify({
        data: properties,
        timestamp: Date.now()
      }));
      
      // Notify Service Worker
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_PROPERTIES',
          payload: { properties }
        });
      }
      
      console.log('[Storage] Properties cached for offline use');
    } catch (error) {
      console.error('Error caching properties:', error);
    }
  },

  // Get cached properties
  getCachedProperties: () => {
    try {
      const cached = localStorage.getItem(PROPERTIES_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Cache valid for 1 hour
        if (Date.now() - timestamp < 60 * 60 * 1000) {
          return data;
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting cached properties:', error);
      return null;
    }
  },

  // Check if online
  isOnline: () => {
    return navigator.onLine;
  },

  // Clear all offline cache
  clearOfflineCache: () => {
    try {
      localStorage.removeItem(FAVORITES_CACHE_KEY);
      localStorage.removeItem(PROPERTIES_CACHE_KEY);
      
      // Notify Service Worker
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_CACHE'
        });
      }
      
      console.log('[Storage] Offline cache cleared');
    } catch (error) {
      console.error('Error clearing offline cache:', error);
    }
  }
};
